import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useThrottleFn } from 'ahooks';
import dynamic from 'next/dynamic';
import Lottie from 'lottie-react';

import TaskItem from '../TaskItem';
import { useWeb3Modal } from '@/components/Web3Modal';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useUserInfo } from 'lib/user';
import {
  DiscordValidationDetail,
  list,
  raffleRegister,
  RequirementValidation,
  TwitterValidationDetail,
} from 'api/raffle/raffle_register';
import { useGetSocialOAuth } from 'api/user_profile/user_profile_social_oauth_url';
import {
  DiscordConfig,
  Raffle,
  refreshRaffleDetails,
} from 'api/raffle/raffle_detail';
import {
  setEntries,
  setEntryCount,
  setDiscordLimit,
  setRaffleMintWallet,
  setRaffleRegisterResult,
} from 'store/features/raffleDetailsSlice';
import { successMsg } from '@/components/PageElement/SuccessMessage';
import { errMsg } from '@/components/PageElement/ErrorMessage';
import {
  setOnlyConnectType,
  setUserProfileInfo,
} from 'store/features/web3Slice';
import ConnectDiscordLabel from './ConnectDiscordLabel';
import ConnectTwitterLabel from './ConnectTwitterLabel';
import AutomateTwitter from './AutomateTwitter';
import MintWallet from './MintWallet';
import classNames from 'classnames';
import { addressFormat } from 'lib/nicknameFormat';
import { Wallet } from 'lib/lucky-x-icons-2';
import EmailLabel from './EmailLabel';
import { raffleEntrySocialParticipate } from 'api/raffle/raffle_entry_social_participate';
import hourglassIcon from 'assets/icons/hourglassIcon.json';
import { limitTimeFormat } from 'lib/limitTimeFormat';

const DiscordLimitTime = dynamic(
  () => import('@/components/RaffleDetails/Tasks/TaskInfo/DiscordLimitTime'),
  {
    ssr: false,
  }
);

interface ITaskInfoProps {
  isShow?: boolean;
}

const TaskInfo: React.FC<ITaskInfoProps> = ({ isShow }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    discord_username,
    discord_avatar,
    twitter_username,
    twitter_avatar,
    isLogin,
    address,
    anti_phishing_code,
    subscriptions,
    email,
    twitter_write_permission,
  } = useUserInfo();

  const {
    raffleInfo,
    raffleRegisterResult: { requirement_validations, validation },
    isOpenTwitterAutomate,
    mintWallet,
    emailAddr,
    validEmailAddr,
    discordLimit,
  } = useAppSelector((state) => state.raffleDetails);
  const { mintWalletListRaffle } = useAppSelector((state) => state.user);
  const [isToVerify, setIsToVerify] = useState(false);
  const { discordOauth, twitterOauth } = useGetSocialOAuth();
  const { toggleWeb3Modal } = useWeb3Modal();
  const [loading, setLoading] = useState(false);
  const [raffle, setRaffle] = useState<Raffle>();

  useEffect(() => {
    setRaffle(raffleInfo);
  }, [raffleInfo]);

  const taskConfig = useCallback(
    (typeValue: string) => {
      return raffle?.requirements?.find((item) => item.type === typeValue);
    },
    [raffle?.requirements]
  );

  const taskValidate = useCallback(
    (typeValue: string) => {
      return requirement_validations?.find(
        (item: RequirementValidation) => item.type === typeValue
      );
    },
    [requirement_validations]
  );

  const verifyStatus = useCallback((verifyStatus: number) => {
    if (verifyStatus === 0) {
      return 'loading';
    } else if (verifyStatus === -1) {
      return 'failed';
    } else if (verifyStatus === 1) {
      return 'success';
    } else if (verifyStatus === -2) {
      return 'limit';
    }
  }, []);

  const toastText = useCallback((dis: boolean, twi: boolean) => {
    if (dis && twi) {
      return 'Twitter and Discord';
    } else if (dis && !twi) {
      return 'Discord';
    } else if (!dis && twi) {
      return 'Twitter';
    }
  }, []);

  const handleRaffleRegister = useCallback(async () => {
    if (
      raffle?.requirement_is_email_open &&
      taskConfig('EMAIL')?.config?.necessary === 'required' &&
      validEmailAddr !== 1
    ) {
      if (validEmailAddr === 0) {
        errMsg('Email is required');
      }
      return;
    }
    const socialParticipateRes = await raffleEntrySocialParticipate(
      Number(raffle?.id)
    );
    if (!socialParticipateRes?.success) {
      errMsg(socialParticipateRes?.fail_msg);
      return;
    }
    setIsToVerify(true);
    setLoading(true);
    const res = await raffleRegister(
      raffle?.vanity_url as string,
      mintWallet,
      isOpenTwitterAutomate,
      emailAddr
    );
    setLoading(false);
    if (res) {
      if (res?.code === 0) {
        const discordOauthValidations = (
          res?.data.requirement_validations.find(
            (item) => item?.type === 'DISCORD'
          )?.detail as list
        )?.is_oauth_canceled;
        const twitterOauthValidations = (
          res?.data.requirement_validations.find(
            (item) => item?.type === 'TWITTER'
          )?.detail as TwitterValidationDetail
        )?.is_oauth_canceled;
        dispatch(setRaffleRegisterResult(res?.data));
        dispatch(setEntries(res?.data?.multiplier));
        dispatch(
          setUserProfileInfo({
            address,
            anti_phishing_code,
            email,
            not_remind_mint: false,
            subscriptions,
            discord_username: discordOauthValidations ? '' : discord_username,
            discord_avatar: discordOauthValidations ? '' : discord_avatar,
            twitter_username: twitterOauthValidations ? '' : twitter_username,
            twitter_avatar: discordOauthValidations ? '' : twitter_avatar,
            twitter_write_permission,
          })
        );
        if (discordOauthValidations || twitterOauthValidations) {
          errMsg(
            `You have removed the ${toastText(
              discordOauthValidations,
              twitterOauthValidations
            )} account, please reconnect your ${toastText(
              discordOauthValidations,
              twitterOauthValidations
            )} account`
          );
        }
        if (res?.data?.validation) {
          const res1 = await refreshRaffleDetails(
            router.query.vanityUrl as string
          );
          dispatch(setEntryCount(res1?.raffle?.entry_count));
          dispatch(setEntries(res1?.raffle?.my_entry?.multiplier as number));
          dispatch(
            setRaffleMintWallet(res1?.raffle?.my_entry?.mint_wallet as string)
          );
        }
        if (
          raffle?.requirement_is_dc_open &&
          !!(
            res?.data?.requirement_validations?.find(
              (item: RequirementValidation) => item.type === 'DISCORD'
            )?.detail as list
          )?.retry_after
        ) {
          errMsg(
            `Please try again in ${limitTimeFormat(
              (
                res?.data?.requirement_validations?.find(
                  (item: RequirementValidation) => item.type === 'DISCORD'
                )?.detail as list
              )?.retry_after as number
            )} if you already a member of the server, since this may be a Discord issue.`
          );
          dispatch(
            setDiscordLimit({
              limitTime: (
                res?.data?.requirement_validations?.find(
                  (item: RequirementValidation) => item.type === 'DISCORD'
                )?.detail as list
              )?.retry_after as number,
              limitMin: '0',
              limitSec: '0',
            })
          );
        }
      } else if (res?.code === 1001) {
        dispatch(
          setRaffleRegisterResult({
            validation: true,
            multiplier: 1,
            requirement_validations: [],
          })
        );
        const res1 = await refreshRaffleDetails(
          router.query.vanityUrl as string
        );
        dispatch(setEntryCount(res1?.raffle?.entry_count));
        dispatch(setEntries(res1?.raffle?.my_entry?.multiplier as number));
        successMsg('You have alreday registered');
      } else {
        errMsg(res?.msg);
      }
    } else {
      dispatch(
        setRaffleRegisterResult({
          validation: false,
          multiplier: 0,
          requirement_validations: [],
        })
      );
      dispatch(setEntries(1));
    }
  }, [
    raffle?.requirement_is_email_open,
    raffle?.id,
    raffle?.vanity_url,
    raffle?.requirement_is_dc_open,
    taskConfig,
    validEmailAddr,
    mintWallet,
    isOpenTwitterAutomate,
    emailAddr,
    dispatch,
    address,
    anti_phishing_code,
    email,
    subscriptions,
    discord_username,
    discord_avatar,
    twitter_username,
    twitter_avatar,
    twitter_write_permission,
    toastText,
    router.query.vanityUrl,
  ]);

  const { run: clickRegister } = useThrottleFn(
    () => {
      handleRaffleRegister();
    },
    { wait: 1500 }
  );

  const verifyWallet = useCallback(() => {
    const hasWalletTask =
      raffle?.requirement_is_wallet_balance_open ||
      raffle?.requirement_is_wallet_nft_open ||
      raffle?.requirement_is_wallet_allow_list_open;
    if (hasWalletTask) {
      let statusBalanceIcon =
        !!raffle?.my_entry || validation ? verifyStatus(1) : null;
      let statusHodlerIcon =
        !!raffle?.my_entry || validation ? verifyStatus(1) : null;
      let statusAllowListIcon =
        !!raffle?.my_entry || validation ? verifyStatus(1) : null;
      if (isToVerify) {
        if (loading) {
          statusBalanceIcon = verifyStatus(0);
          statusHodlerIcon = verifyStatus(0);
          statusAllowListIcon = verifyStatus(0);
        } else {
          if (
            raffle?.requirement_is_wallet_balance_open &&
            taskValidate('WALLET_BALANCE')?.validation
          ) {
            statusBalanceIcon = verifyStatus(1);
          } else {
            statusBalanceIcon = verifyStatus(-1);
          }
          if (
            raffle?.requirement_is_wallet_nft_open &&
            taskValidate('WALLET_NFT')?.validation
          ) {
            statusHodlerIcon = verifyStatus(1);
          } else {
            statusHodlerIcon = verifyStatus(-1);
          }
          if (
            raffle?.requirement_is_wallet_allow_list_open &&
            taskValidate('WALLET_ALLOW_LIST')?.validation
          ) {
            statusAllowListIcon = verifyStatus(1);
          } else {
            statusAllowListIcon = verifyStatus(-1);
          }
        }
      }
      return (
        <div className="">
          <div className="flex items-center  justify-between">
            <div className="flex items-center gap-1">
              <Wallet size={24} />
              <div>Signed wallet</div>
            </div>
            <div>
              {!isLogin || (isLogin && hasWalletTask && !address) ? (
                <div
                  className="flex h-8 w-[100px] cursor-pointer items-center justify-center gap-1 rounded-full bg-white"
                  onClick={() => {
                    toggleWeb3Modal();
                    dispatch(setOnlyConnectType('ETH'));
                  }}
                >
                  <Wallet size={20} color="#27282B" />
                  <div className="text-sm font-normal text-[#111]">Connect</div>
                </div>
              ) : (
                <div className="font-semibold">{addressFormat(address)}</div>
              )}
            </div>
          </div>
          {raffle?.requirement_is_wallet_balance_open && (
            <TaskItem
              verifyStatus={statusBalanceIcon}
              icon={'walletBalance'}
            ></TaskItem>
          )}
          {raffle?.requirement_is_wallet_nft_open && (
            <TaskItem
              verifyStatus={statusHodlerIcon}
              icon={'walletHodler'}
            ></TaskItem>
          )}
          {raffle?.requirement_is_wallet_allow_list_open && (
            <TaskItem
              verifyStatus={statusAllowListIcon}
              icon={'walletAllowList'}
            ></TaskItem>
          )}
        </div>
      );
    }
    return null;
  }, [
    address,
    dispatch,
    isLogin,
    isToVerify,
    loading,
    raffle?.my_entry,
    raffle?.requirement_is_wallet_allow_list_open,
    raffle?.requirement_is_wallet_balance_open,
    raffle?.requirement_is_wallet_nft_open,
    taskValidate,
    toggleWeb3Modal,
    validation,
    verifyStatus,
  ]);

  const statusJoinIcon = useCallback(
    (index: number) => {
      if (!!raffle?.my_entry || validation) {
        return verifyStatus(1);
      } else {
        if (isToVerify) {
          if (loading) {
            return verifyStatus(0);
          } else {
            if (
              taskValidate('DISCORD')?.detail &&
              (
                (taskValidate('DISCORD')?.detail as list)
                  ?.list as DiscordValidationDetail[]
              )[index]?.validation
            ) {
              return verifyStatus(1);
            } else {
              if (
                taskValidate('DISCORD')?.detail &&
                !!(
                  (taskValidate('DISCORD')?.detail as list)
                    ?.list as DiscordValidationDetail[]
                )[index]?.exp_map?.too_many_request
              ) {
                return verifyStatus(-2);
              } else {
                return verifyStatus(-1);
              }
            }
          }
        } else {
          return null;
        }
      }
    },
    [
      isToVerify,
      loading,
      raffle?.my_entry,
      taskValidate,
      validation,
      verifyStatus,
    ]
  );

  const verifyDiscord = useCallback(() => {
    if (raffle?.requirement_is_dc_open) {
      return (
        <div className="">
          {(taskConfig('DISCORD')?.config as DiscordConfig[])
            ?.filter((itemI) => !!itemI?.guild?.server_name)
            ?.map((item: DiscordConfig, index) => (
              <TaskItem
                key={index}
                verifyStatus={statusJoinIcon(index)}
                icon={'joinDiscord'}
                itemDiscord={item}
              ></TaskItem>
            ))}
        </div>
      );
    }
    return null;
  }, [raffle?.requirement_is_dc_open, taskConfig, statusJoinIcon]);

  const verifyTwitter = useCallback(() => {
    if (raffle?.requirement_is_tw_open) {
      let statusFollowIcon =
        !!raffle?.my_entry || validation ? verifyStatus(1) : null;
      let statusLikeRetweetIcon =
        !!raffle?.my_entry || validation ? verifyStatus(1) : null;
      if (isToVerify) {
        if (loading) {
          statusFollowIcon = verifyStatus(0);
          statusLikeRetweetIcon = verifyStatus(0);
        } else {
          if (
            !!taskConfig('TWITTER')?.config?.follow_users &&
            (taskValidate('TWITTER')?.detail as TwitterValidationDetail)?.follow
          ) {
            statusFollowIcon = verifyStatus(1);
          } else {
            statusFollowIcon = verifyStatus(-1);
          }
          if (
            !!taskConfig('TWITTER')?.config?.retweet_and_like &&
            (taskValidate('TWITTER')?.detail as TwitterValidationDetail)
              ?.retweet_and_like
          ) {
            statusLikeRetweetIcon = verifyStatus(1);
          } else {
            statusLikeRetweetIcon = verifyStatus(-1);
          }
        }
      }
      return (
        <div className="">
          {!!taskConfig('TWITTER')?.config?.follow_users &&
            taskConfig('TWITTER')?.config?.follow_users?.length !== 0 && (
              <TaskItem
                verifyStatus={statusFollowIcon}
                icon={'followTwitter'}
              ></TaskItem>
            )}
          {!!taskConfig('TWITTER')?.config?.retweet_and_like && (
            <TaskItem
              verifyStatus={statusLikeRetweetIcon}
              icon={'likeRetweet'}
            ></TaskItem>
          )}
        </div>
      );
    }
    return null;
  }, [
    raffle?.requirement_is_tw_open,
    raffle?.my_entry,
    validation,
    verifyStatus,
    isToVerify,
    taskConfig,
    loading,
    taskValidate,
  ]);

  const buttonStatus = useCallback(() => {
    if (isShow) {
      return;
    }
    const hasWalletTask =
      raffle?.requirement_is_wallet_balance_open ||
      raffle?.requirement_is_wallet_nft_open ||
      raffle?.requirement_is_wallet_allow_list_open;
    if (!isLogin || (isLogin && hasWalletTask && !address)) {
      return (
        <div
          className="mt-6 flex h-12 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-base font-medium text-neutral-100 hover:bg-primary-600"
          onClick={() => {
            toggleWeb3Modal();
            if (hasWalletTask) {
              dispatch(setOnlyConnectType('ETH'));
            } else {
              dispatch(setOnlyConnectType(''));
            }
          }}
        >
          Connect wallet
        </div>
      );
    } else if (!discord_username) {
      return (
        <div
          className="mt-6 flex h-12 cursor-pointer items-center justify-center rounded-full  bg-primary-500 text-base font-medium text-neutral-100 hover:bg-primary-600"
          onClick={() => {
            discordOauth();
          }}
        >
          Connect discord
        </div>
      );
    } else if (!twitter_username) {
      return (
        <div
          className="mt-6 flex h-12 cursor-pointer items-center justify-center rounded-full  bg-primary-500 text-base font-medium text-neutral-100 hover:bg-primary-600"
          onClick={() => {
            twitterOauth();
          }}
        >
          Connect twitter
        </div>
      );
    } else {
      if (raffle?.status === 'NOT_START') {
        return (
          <div className="mt-6 flex h-12 cursor-not-allowed items-center justify-center rounded-full bg-[#C8C8C8] text-base font-medium text-neutral-900">
            Not open yet
          </div>
        );
      } else if (raffle?.status === 'ACTIVE') {
        return (
          <div
            className={classNames(
              'active:buttonShadow mt-6 flex h-12 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-base font-medium text-neutral-100 hover:bg-primary-600',
              {
                'automateBg text-neutral-900': isOpenTwitterAutomate,
                'bg-primary-500 text-neutral-100': !isOpenTwitterAutomate,
              }
            )}
            onClick={
              loading
                ? () => {}
                : () => {
                    if (discordLimit?.limitTime !== 0) {
                      errMsg(
                        `Due to much Discord and Twitter requests, please try again in ${discordLimit?.limitMin}:${discordLimit?.limitSec}`
                      );
                    } else {
                      if (
                        !mintWallet &&
                        !!mintWalletListRaffle &&
                        mintWalletListRaffle?.length !== 0
                      ) {
                        errMsg(
                          'Please wait for the Mint wallet to load successfully before clicking Register.'
                        );
                      } else if (mintWalletListRaffle?.length === 0) {
                        errMsg(
                          'Please add mint wallet first before clicking Register.'
                        );
                      } else if (!!mintWallet) {
                        clickRegister();
                      }
                    }
                  }
            }
          >
            {loading ? (
              'Registering'
            ) : (
              <>
                {discordLimit?.limitTime !== 0 && (
                  <>
                    <Lottie
                      animationData={hourglassIcon}
                      style={{ width: 16 }}
                    />
                    &nbsp;
                  </>
                )}
                Register{' '}
                {discordLimit?.limitTime !== 0 && (
                  <>
                    in&nbsp;
                    <DiscordLimitTime time={discordLimit?.limitTime} />
                  </>
                )}
              </>
            )}
          </div>
        );
      }
    }
  }, [
    isShow,
    raffle?.requirement_is_wallet_balance_open,
    raffle?.requirement_is_wallet_nft_open,
    raffle?.requirement_is_wallet_allow_list_open,
    raffle?.status,
    isLogin,
    address,
    discord_username,
    twitter_username,
    toggleWeb3Modal,
    dispatch,
    discordOauth,
    twitterOauth,
    isOpenTwitterAutomate,
    loading,
    discordLimit?.limitTime,
    discordLimit?.limitMin,
    discordLimit?.limitSec,
    mintWallet,
    mintWalletListRaffle,
    clickRegister,
  ]);

  if (
    raffle?.status !== 'NOT_START' &&
    raffle?.status !== 'ACTIVE' &&
    !isShow
  ) {
    return null;
  }

  return (
    <div
      className={classNames('rounded-2xl font-rubik', {
        'pt-6': isShow,
      })}
    >
      <div className="grid grid-cols-1 gap-6 text-base font-normal text-neutral-100">
        {verifyWallet()}
        <div>
          <ConnectDiscordLabel />
          {verifyDiscord()}
        </div>
        <div>
          <ConnectTwitterLabel />
          {verifyTwitter()}
          {/* <AutomateTwitter isShow={!!isShow} /> */}
        </div>
        <MintWallet isShow={!!isShow} />
        <EmailLabel />
      </div>
      {buttonStatus()}
    </div>
  );
};

export default TaskInfo;
